import React, { useState, useEffect } from 'react';
import { RotateCw } from 'lucide-react';
import quizData from '../data/quizQuestions.json';

const Quiz = () => {
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showScore, setShowScore] = useState(false);
    const [score, setScore] = useState(0);
    const [answered, setAnswered] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState(null);

    const shuffleAndRestart = () => {
        const validQuestions = quizData.filter(q => q && Array.isArray(q.answerOptions));
        const shuffled = validQuestions.sort(() => Math.random() - 0.5);
        
        shuffled.forEach(q => {
            if (q.answerOptions) {
                q.answerOptions.sort(() => Math.random() - 0.5);
            }
        });
        
        setQuestions(shuffled);
        setCurrentIndex(0);
        setScore(0);
        setShowScore(false);
        setAnswered(false);
        setSelectedAnswer(null);
    };

    useEffect(() => {
        shuffleAndRestart();
    }, []);

    const handleAnswerOptionClick = (answerOption) => {
        if (answered) return;
        setSelectedAnswer(answerOption);
        setAnswered(true);
        if (answerOption.isCorrect) {
            setScore(prevScore => prevScore + 1);
        }
    };

    const handleNextClick = () => {
        const nextIndex = currentIndex + 1;
        if (nextIndex < questions.length) {
            setCurrentIndex(nextIndex);
            setAnswered(false);
            setSelectedAnswer(null);
        } else {
            setShowScore(true);
        }
    };
    
    if (questions.length === 0) {
        return <div className="text-center p-8">Loading Quiz...</div>;
    }

    const activeQuestion = questions[currentIndex];

    const getButtonClass = (answerOption) => {
        if (!answered) {
            return 'bg-white dark:bg-gray-900 hover:bg-blue-100 dark:hover:bg-gray-700';
        }
        if (answerOption.isCorrect) {
            return 'bg-green-200 dark:bg-green-800 border-green-500';
        }
        if (selectedAnswer === answerOption) {
            return 'bg-red-200 dark:bg-red-800 border-red-500';
        }
        return 'bg-gray-100 dark:bg-gray-800 opacity-60';
    };

    return (
        <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-lg shadow-inner border border-gray-200 dark:border-gray-700">
            {/* --- MODIFICATION: Full JSX for the quiz is now included --- */}
            {showScore ? (
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-cyan-400 mb-4">Quiz Complete!</h2>
                    <p className="text-xl text-gray-700 dark:text-gray-300 mb-6">
                        You scored {score} out of {questions.length}
                    </p>
                    <button
                        onClick={shuffleAndRestart}
                        className="flex items-center justify-center mx-auto px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold shadow-md hover:bg-blue-700 transition duration-200"
                    >
                        <RotateCw size={18} className="mr-2" />
                        Restart Quiz
                    </button>
                </div>
            ) : (
                <>
                    <div className="mb-6">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
                            Question {currentIndex + 1}/{questions.length}
                        </h2>
                        <p className="text-lg text-gray-900 dark:text-cyan-400">{activeQuestion.questionText}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {activeQuestion.answerOptions.map((answerOption, index) => (
                            <button
                                key={index}
                                onClick={() => handleAnswerOptionClick(answerOption)}
                                disabled={answered}
                                className={`w-full text-left p-4 rounded-lg shadow-md transition-all duration-200 border border-gray-200 dark:border-gray-700 focus:outline-none ${getButtonClass(answerOption)}`}
                            >
                                <span className="text-gray-800 dark:text-gray-200">{answerOption.answerText}</span>
                            </button>
                        ))}
                    </div>

                    {answered && (
                        <div className="mt-6 p-4 bg-blue-100 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                            <h3 className="font-bold text-blue-800 dark:text-blue-300 mb-2">Explanation:</h3>
                            <p className="text-blue-700 dark:text-blue-300">{activeQuestion.explanation}</p>
                            <button
                                onClick={handleNextClick}
                                className="w-full mt-4 py-2 px-4 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Quiz;